import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { schema, normalize } from 'normalizr';
import { selectAvailableLocales } from 'models/localization';
import { eventsAdapter } from './selectors';
import { fetchXtraEventRequest, FetchBoothTaxonomiesQueryProps, fetchBoothTaxonomiesQueryRequest } from './requests';
import { CMSEvent } from './types';
import { mapLocalizable } from '../common/utils';

const eventEntity = new schema.Entity('events', {}, {
  processStrategy(entity) {
    return mapLocalizable(entity);
  },
});

export const fetchBoothTaxonomies = createAsyncThunk(
  'events/fetchBoothTaxonomies',
  async ({ boothTaxonomiesCursor }: FetchBoothTaxonomiesQueryProps = {}, { getState, dispatch }) => {
    try {
      const state = getState();
      const locales = selectAvailableLocales(state);
      const response = await fetchBoothTaxonomiesQueryRequest({ locales, boothTaxonomiesCursor });

      const { pageInfo } = response.boothTaxonomies;
      delete response.boothTaxonomies.pageInfo;
      if (pageInfo.hasNextPage) dispatch(fetchBoothTaxonomies({ boothTaxonomiesCursor: pageInfo.endCursor }));

      response.boothTaxonomies.nodes = response.boothTaxonomies.nodes.filter((it: any) => Object.keys(it).length);
      const { entities } = normalize<CMSEvent>([response], [eventEntity]);
      return entities.events;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
);

export const fetchXtraEvent = createAsyncThunk(
  'events/fetchXtraEvent',
  async () => {
    try {
      const response = await fetchXtraEventRequest();
      const { entities } = normalize<CMSEvent>([response], [eventEntity]);
      return entities.events;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
);

export const eventsSlice = createSlice({
  name: 'events',
  initialState: eventsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchXtraEvent.fulfilled, (state, action) => {
      eventsAdapter.upsertMany(state, action.payload);
    });
    builder.addCase(fetchBoothTaxonomies.fulfilled, (state, action) => {
      const updates = Object.entries(action.payload)
        .map(([key, value]) => ({
          id: key,
          changes: {
            boothTaxonomies: [
              ...(state.entities[key]?.boothTaxonomies || []),
              ...value.boothTaxonomies,
            ],
          },
        }));
      eventsAdapter.updateMany(state, updates);
    });
  },
});

export default eventsSlice.reducer;
